import type { FilmProject, ProductionPackageHistoryItem, UploadedAssetRecord } from "../types";

/** The frontend warns when a running server reports an older feature level than it expects. */
export const EXPECTED_ENGINE_FEATURE_LEVEL = 10;

export interface ApiHealth {
  ok: boolean;
  service: string;
  featureLevel?: number;
  storage?: {
    projects?: boolean;
    assets?: boolean;
  };
}

export interface CloudSaveResponse {
  ok?: boolean;
  status?: string;
  message?: string;
  projectId?: string;
  savedAt?: string;
  project?: FilmProject;
  meta?: Record<string, unknown>;
}

export interface CloudProjectResponse {
  ok?: boolean;
  status?: string;
  message?: string;
  project?: FilmProject;
  meta?: Record<string, unknown>;
}

export interface CloudProjectSummary {
  id: string;
  title: string;
  goal?: string;
  useCase?: string;
  runtimeSeconds?: number;
  aspectRatio?: string;
  resolution?: string;
  updatedAt?: string;
  createdAt?: string;
  scriptFileName?: string;
  productionDocFileName?: string;
  sceneCount?: number;
  frameCount?: number;
  uploadedAssetCount?: number;
  pendingUploadCount?: number;
  failedUploadCount?: number;
  localOnlyAssetCount?: number;
  generatedFrameCount?: number;
  providerReady?: boolean;
  productionPackageCount?: number;
  cloudSynced?: boolean;
}

export interface CloudProjectListResponse {
  ok?: boolean;
  status?: string;
  message?: string;
  workspaceId?: string;
  projects?: CloudProjectSummary[];
}

export interface AssetUploadResponse {
  ok?: boolean;
  status?: string;
  message?: string;
  asset?: UploadedAssetRecord & {
    url?: string;
    type?: string;
  };
}

export interface ProjectPackageResponse {
  ok?: boolean;
  status?: string;
  message?: string;
  projectId?: string;
  packages?: ProductionPackageHistoryItem[];
  latest?: ProductionPackageHistoryItem | null;
  package?: ProductionPackageHistoryItem;
  version?: number;
  versionLabel?: string;
  sourcePackageId?: string;
  sourceVersion?: number;
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  status: number;
  payload: Record<string, unknown>;

  constructor(message: string, status: number, payload: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

async function readJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};
  if (!response.ok) {
    const message = typeof payload?.error === "string"
      ? payload.error
      : typeof payload?.message === "string"
        ? payload.message
        : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, payload && typeof payload === "object" ? payload : {});
  }
  return payload as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: "application/json" } });
  return readJson<T>(response);
}

export async function apiPost<T>(path: string, payload: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  return readJson<T>(response);
}

export async function apiDelete<T>(path: string, payload: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  return readJson<T>(response);
}

export async function getApiHealth(): Promise<ApiHealth> {
  return apiGet<ApiHealth>("/api/health");
}

export async function analyzeFilmProject(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/analyze", payload);
}

export async function buildStoryboard(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/storyboard", payload);
}

export async function generateCharacterCards(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/character-cards", payload);
}

export async function generateSceneReferences(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/scene-references", payload);
}

export async function buildProductionPackage(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/production-package", payload);
}

export async function generatePoster(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/poster", payload);
}

export interface ShareCreateResponse {
  ok: boolean;
  status: string;
  token?: string;
  message?: string;
}

export interface ShareSnapshotResponse {
  ok: boolean;
  status: string;
  title?: string;
  createdAt?: string;
  project?: Record<string, unknown>;
  message?: string;
}

/** Publish a read-only snapshot of the project; anyone with the token can watch the cut. */
export async function createShareSnapshot(project: Record<string, unknown>): Promise<ShareCreateResponse> {
  return apiPost<ShareCreateResponse>("/api/share", { project });
}

export async function fetchShareSnapshot(token: string): Promise<ShareSnapshotResponse> {
  return apiGet<ShareSnapshotResponse>(`/api/share/${encodeURIComponent(token)}`);
}

/* ---- Render gate: the only doorway to live EvoLink/Seedance spend ---- */

export interface RenderBalance {
  tokenRemaining: number;
  tokenUnlimited: boolean;
  userRemaining: number;
}

export interface RenderGateStatus {
  ok: boolean;
  keyPresent: boolean;
  dryRunOnly: boolean;
  model: string;
  estimateRatePerSecond: number;
  publicAssetBase: string;
  balance: RenderBalance | null;
}

export interface RenderEstimateChunk {
  chunkId: string;
  label: string;
  durationSeconds: number;
  prompt: string;
  imageUrls: string[];
  audioUrls: string[];
  estimatedCredits: number;
  /** Per-chunk audio: vignettes speak (true), VO beats stay silent (false). Absent = job default. */
  generateAudio?: boolean;
}

export interface RenderEstimate {
  ok: boolean;
  status: string;
  model: string;
  quality: string;
  aspectRatio: string;
  chunks: RenderEstimateChunk[];
  totalSeconds: number;
  estimatedCredits: number;
  estimateRatePerSecond: number;
  balance: RenderBalance | null;
  warnings: string[];
  note: string;
}

export interface RenderTask {
  chunkId: string;
  label: string;
  durationSeconds: number;
  status: string;
  taskId: string;
  creditsReserved: number;
  videoKey: string;
  error: string;
  progress?: number;
  resultUrl?: string;
}

export interface RenderJob {
  id: string;
  createdAt: string;
  projectId: string;
  title: string;
  model: string;
  quality: string;
  aspectRatio: string;
  approvedCapCredits: number;
  reservedCredits: number;
  status: string;
  stopReason: string;
  tasks: RenderTask[];
}

export async function fetchRenderStatus(): Promise<RenderGateStatus> {
  return apiGet<RenderGateStatus>("/api/render/status");
}

export async function requestRenderEstimate(payload: Record<string, unknown>): Promise<RenderEstimate> {
  return apiPost<RenderEstimate>("/api/render/estimate", payload);
}

export async function startRenderJob(payload: Record<string, unknown>): Promise<{ ok: boolean; status: string; message?: string; job?: RenderJob }> {
  return apiPost<{ ok: boolean; status: string; message?: string; job?: RenderJob }>("/api/render/start", payload);
}

export async function fetchRenderJob(jobId: string): Promise<{ ok: boolean; job?: RenderJob }> {
  return apiGet<{ ok: boolean; job?: RenderJob }>(`/api/render/jobs/${encodeURIComponent(jobId)}`);
}

export async function stitchRenderJobOnEngine(payload: Record<string, unknown>): Promise<{ ok: boolean; status: string; message?: string; storageKey?: string; bytes?: number }> {
  return apiPost<{ ok: boolean; status: string; message?: string; storageKey?: string; bytes?: number }>("/api/render/stitch", payload);
}

/* ---- Phase 1: doctor, stitch jobs, exports ---- */

export interface DoctorCheck {
  id: string;
  label: string;
  ok: boolean;
  detail: string;
  fix: string;
}

export interface DoctorReport {
  ok: boolean;
  featureLevel: number;
  startedAt: string;
  commit: string;
  committedAgo: string;
  capabilities: Record<string, boolean>;
  checks: DoctorCheck[];
  allOk: boolean;
}

export async function fetchDoctor(): Promise<DoctorReport> {
  return apiGet<DoctorReport>("/api/doctor");
}

export interface StitchJobRecord {
  id: string;
  jobId: string;
  title: string;
  status: "running" | "done" | "failed";
  createdAt: string;
  progress: { step: string; segment: number; total: number };
  result: { storageKey?: string; bytes?: number; notes?: string[] } | null;
  message: string;
}

export async function startStitchJobOnEngine(payload: Record<string, unknown>): Promise<{ ok: boolean; status: string; stitchId?: string; message?: string }> {
  return apiPost<{ ok: boolean; status: string; stitchId?: string; message?: string }>("/api/render/stitch/start", payload);
}

export async function fetchStitchJob(stitchId: string): Promise<{ ok: boolean; stitch?: StitchJobRecord }> {
  return apiGet<{ ok: boolean; stitch?: StitchJobRecord }>(`/api/render/stitch/jobs/${encodeURIComponent(stitchId)}`);
}

export interface ProjectExportItem {
  storageKey: string;
  fileName: string;
  bytes: number;
  modifiedAt: string;
  isMaster: boolean;
}

export async function fetchProjectExports(projectId: string): Promise<{ ok: boolean; items: ProjectExportItem[]; folder: string }> {
  return apiGet<{ ok: boolean; items: ProjectExportItem[]; folder: string }>(`/api/projects/${encodeURIComponent(projectId)}/exports`);
}

export interface RenderQcIssue {
  severity: "fail" | "warn" | "info";
  type: string;
  at: string;
  message: string;
  fix: string;
}

export interface RenderQcReport {
  ok: boolean;
  status: string;
  storageKey: string;
  fileName: string;
  isMaster: boolean;
  score: number;
  verdict: "pass" | "review" | "rerender";
  issues: RenderQcIssue[];
  media: Record<string, unknown>;
  artifacts: { contactSheetKey?: string };
  vision?: Record<string, unknown> | null;
  checkedAt: string;
  message?: string;
}

export async function requestRenderQc(payload: Record<string, unknown>): Promise<RenderQcReport> {
  return apiPost<RenderQcReport>("/api/render/qc", payload);
}

/** Smart Ingest: low-detail vision classification of an uploaded image. */
export interface AssetClassification {
  ok: boolean;
  status: string;
  message?: string;
  bucket?: string;
  characterName?: string;
  placeName?: string;
  lookLabel?: string;
  confidence?: number;
  reason?: string;
}

export async function classifyAsset(payload: Record<string, unknown>): Promise<AssetClassification> {
  return apiPost<AssetClassification>("/api/assets/classify", payload);
}

/** Remove an uploaded asset's stored bytes (engine disk or R2). */
export async function deleteAssetObject(storageKey: string): Promise<{ ok: boolean; status?: string }> {
  const response = await fetch(`/api/assets/object/${storageKey.split("/").map(encodeURIComponent).join("/")}`, { method: "DELETE" });
  return response.json() as Promise<{ ok: boolean; status?: string }>;
}

/* ---- Objective Cuts: the kinetix.film offerings as draftable beat grammars ---- */

export interface CutObjectiveSection {
  id: string;
  purpose: string;
  share: number;
}

export interface CutObjective {
  id: string;
  label: string;
  target: number;
  range: [number, number];
  aspect: string;
  blurb: string;
  grammar: CutObjectiveSection[];
  audioNote: string;
}

export interface DailiesReport {
  ok: boolean;
  status: string;
  storageKey?: string;
  frameCount?: number;
  frameTimestamps?: string[];
  notes?: Record<string, unknown>;
  screenedAt?: string;
  message?: string;
}

export async function requestDailies(
  storageKey: string,
  context: Record<string, unknown>,
  opts: { style?: "master" | "clip" | "audience"; frameCount?: number } = {}
): Promise<DailiesReport> {
  return apiPost<DailiesReport>("/api/dailies", { storageKey, context, ...opts });
}

export interface DerivedItem {
  storageKey: string;
  bytes: number;
  kind: string;
}

export async function deriveExports(args: {
  storageKey?: string;
  projectId: string;
  formats: string[];
  look?: Record<string, unknown>;
}): Promise<{ ok: boolean; status: string; items?: DerivedItem[]; notes?: string[]; message?: string }> {
  return apiPost("/api/exports/derive", args);
}

export async function requestCaptions(storageKey: string): Promise<{
  ok: boolean;
  status: string;
  storageKey?: string;
  srt?: string;
  message?: string;
}> {
  return apiPost("/api/captions", { storageKey });
}

export async function requestTableRead(args: {
  projectId: string;
  sceneId: string;
  title: string;
  lines: Array<{ speaker: string; text: string }>;
}): Promise<{ ok: boolean; status: string; storageKey?: string; message?: string; lines?: number }> {
  return apiPost("/api/tableread", args);
}

export async function requestVoiceLabel(args: {
  storageKey: string;
  fileName: string;
  characters: string[];
}): Promise<{ ok: boolean; status: string; characterName?: string; label?: string; confidence?: number; transcript?: string; message?: string }> {
  return apiPost("/api/voice/label", args);
}

export async function requestAnimatic(args: {
  projectId: string;
  title: string;
  musicKey?: string;
  scenes: Array<{ sceneId: string; seconds: number; boards: string[]; lines: Array<{ speaker: string; text: string }> }>;
}): Promise<{ ok: boolean; status: string; storageKey?: string; scenes?: number; seconds?: number; voiced?: boolean; notes?: string[]; message?: string }> {
  return apiPost("/api/animatic", args);
}

export async function requestMusicBeats(storageKey: string): Promise<{ ok: boolean; status: string; beats?: number[]; message?: string }> {
  return apiPost("/api/music/beats", { storageKey });
}

export interface RenderSpend {
  ok: boolean;
  totalCredits?: number;
  renders?: number;
  items?: Array<{ jobId: string; createdAt: string; status: string; credits: number; tasks: number }>;
  message?: string;
}

export async function fetchRenderHistory(projectId: string): Promise<RenderSpend> {
  return apiGet<RenderSpend>(`/api/render/history/${encodeURIComponent(projectId)}`);
}

export interface BoardQcResult {
  ok: boolean;
  status: string;
  storageKey?: string;
  pass?: boolean;
  severity?: "ok" | "minor" | "major" | string;
  issues?: string[];
  note?: string;
  message?: string;
}

export async function requestBoardQc(args: {
  storageKey: string;
  prompt?: string;
  sceneHeading?: string;
  characters?: string[];
}): Promise<BoardQcResult> {
  return apiPost<BoardQcResult>("/api/boards/qc", args);
}

export interface CastingMatch {
  storageKey: string;
  fileName: string;
  characterName: string;
  confidence: number;
  why: string;
}

export async function requestCasting(
  characters: Array<{ name: string; description: string }>,
  assets: Array<{ storageKey: string; fileName: string }>
): Promise<{ ok: boolean; status: string; matches?: CastingMatch[]; roles?: string[]; message?: string }> {
  return apiPost("/api/casting", { characters, assets });
}

export async function fetchObjectives(): Promise<{ ok: boolean; objectives: CutObjective[] }> {
  return apiGet<{ ok: boolean; objectives: CutObjective[] }>("/api/objectives");
}

/* ---- Phase 2: the director's copilot (proposes; never renders, never spends) ---- */

export interface AssistantBeatDraft {
  id: string;
  label: string;
  seconds: number;
  speak: boolean;
  line: string;
  refHints: string[];
  prompt: string;
}

export async function askAssistant(
  mode: "explain" | "beats" | "prompt" | "cut" | "coverage" | "pitch" | "bible" | "gaps" | "draft" | "chat",
  context: Record<string, unknown>
): Promise<{ ok: boolean; status: string; message?: string; answer?: Record<string, unknown> }> {
  return apiPost<{ ok: boolean; status: string; message?: string; answer?: Record<string, unknown> }>("/api/assistant", { mode, context });
}

export async function listProjectPackages(projectId: string, accessKey: string): Promise<ProjectPackageResponse> {
  const query = new URLSearchParams({ accessKey });
  return apiGet<ProjectPackageResponse>(`/api/projects/${encodeURIComponent(projectId)}/packages?${query.toString()}`);
}

export async function saveProjectPackageVersion(
  projectId: string,
  accessKey: string,
  item: ProductionPackageHistoryItem
): Promise<ProjectPackageResponse> {
  return apiPost<ProjectPackageResponse>(`/api/projects/${encodeURIComponent(projectId)}/packages`, { accessKey, package: item });
}

export async function getProjectPackageVersion(projectId: string, version: string, accessKey: string): Promise<ProjectPackageResponse> {
  const query = new URLSearchParams({ accessKey });
  return apiGet<ProjectPackageResponse>(`/api/projects/${encodeURIComponent(projectId)}/packages/${encodeURIComponent(version)}?${query.toString()}`);
}

export async function duplicateProjectPackageVersion(
  projectId: string,
  version: string,
  accessKey: string,
  title?: string
): Promise<ProjectPackageResponse> {
  return apiPost<ProjectPackageResponse>(`/api/projects/${encodeURIComponent(projectId)}/packages/${encodeURIComponent(version)}/duplicate`, { accessKey, title });
}

export async function parseReferenceBoard(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>("/api/reference-board", payload);
}

export async function uploadProjectAsset(
  projectId: string,
  file: File,
  context: Partial<UploadedAssetRecord> = {}
): Promise<AssetUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  form.append("projectId", projectId);
  form.append("kind", context.kind || "asset");
  form.append("label", context.label || file.name.replace(/\.[^.]+$/, ""));
  if (context.sceneId) form.append("sceneId", context.sceneId);
  if (context.characterName) form.append("characterName", context.characterName);
  if (context.sourceOfTruth !== undefined) form.append("sourceOfTruth", String(context.sourceOfTruth));
  if (context.locked !== undefined) form.append("locked", String(context.locked));
  if (context.sourceMode) form.append("sourceMode", context.sourceMode);
  if (context.provider) form.append("provider", context.provider);

  const response = await fetch("/api/assets", {
    method: "POST",
    headers: { Accept: "application/json" },
    body: form
  });
  return readJson<AssetUploadResponse>(response);
}

export async function saveCloudProject(
  project: FilmProject,
  workspaceId: string,
  accessKey: string,
  baseUpdatedAt?: string
): Promise<CloudSaveResponse> {
  return apiPost<CloudSaveResponse>(`/api/projects/${encodeURIComponent(project.id)}`, {
    workspaceId,
    accessKey,
    baseUpdatedAt,
    project,
    meta: {
      title: project.title,
      goal: project.goal,
      useCase: project.goal,
      runtimeSeconds: project.runtimeSeconds,
      aspectRatio: project.aspectRatio,
      resolution: project.resolution,
      sceneCount: project.scenes.length,
      frameCount: project.scenes.reduce((total, scene) => total + scene.frames.length, 0),
      scriptFileName: project.scriptUpload?.fileName || "",
      productionPackageCount: project.productionPackages?.length || (project.lastProductionPackage ? 1 : 0),
      uploadedAssetCount: Object.values(project.uploadedAssets || {}).reduce((total, bucket) => total + bucket.length, 0),
      generatedFrameCount: project.scenes.reduce((total, scene) => total + scene.frames.filter((frame) => frame.generationStatus === "generated").length, 0),
      providerReady: Boolean(project.lastProductionPackage || project.productionPackages?.length)
    }
  });
}

export async function loadCloudProject(projectId: string, accessKey: string): Promise<CloudProjectResponse> {
  const query = new URLSearchParams({ accessKey });
  return apiGet<CloudProjectResponse>(`/api/projects/${encodeURIComponent(projectId)}?${query.toString()}`);
}

export async function listCloudProjects(workspaceId: string, accessKey?: string): Promise<CloudProjectListResponse> {
  const query = new URLSearchParams({ workspaceId });
  if (accessKey) query.set("accessKey", accessKey);
  return apiGet<CloudProjectListResponse>(`/api/projects?${query.toString()}`);
}

export async function deleteCloudProject(projectId: string, accessKey: string): Promise<CloudSaveResponse> {
  return apiDelete<CloudSaveResponse>(`/api/projects/${encodeURIComponent(projectId)}`, { accessKey });
}
